import { useAppStore } from "@hooks/useAppStore";
import { useAppTemplate } from "@hooks/useAppTemplate";
import useMixedItemDist from "@hooks/useMixedItemDist";
import type { Section as HomeSection } from "@memorang/ui/components/HomeApplet/types";
import type { RecommendedSkillTask } from "../../recommendations/adapters/dashboard-task-types";
import { useItemTypesSection } from "../use-item-types-section";
import { useFeaturesSection } from "../useFeaturesSection";
import { useHasAccess } from "../useHasAccess";
import useItemsCount from "../useItemsCount";
import { useMasteryKeyTopicsSectionData } from "../useMasteryKeyTopicsSectionData";
import { useRecommendedTasksSection } from "../useRecommendedTasksSection";
import { generatePracticeTestSubtitle } from "./helpers";

type Params = {
	onSelectTags?: (tagIds: string[]) => void;
	onStartRecommendedSession?: (task: RecommendedSkillTask) => void;
};

export const useHomeSections = ({
	onSelectTags,
	onStartRecommendedSession,
}: Params = {}) => {
	const { hasStudyPackContentAccess } = useHasAccess();
	const { data: studyPackData, isLoading: isLoadingStudyPackData } =
		useMixedItemDist({
			action: "GET_STUDYPACK_DISTRIBUTION",
		});

	const { isFetchingItemsCount, numPracticeTests, numQuiz } = useItemsCount();

	const diagnosticLabel = useAppStore((state) => state.exam.diagnosticLabel);
	const practiceTestLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);

	const tasksSectionData = useRecommendedTasksSection({
		studyPackData,
		isLoadingStudyPackData,
		onSelectTags,
		onStartRecommendedSession,
	});

	const productsSectionData = useFeaturesSection({
		isFetchingItemsCount,
		practiceTestSubtitle: generatePracticeTestSubtitle({
			numPracticeTests,
			numQuiz,
			practiceTestLabel,
			diagnosticLabel,
		}),
		numPracticeTests,
	});

	const topicsSectionData = useMasteryKeyTopicsSectionData(
		studyPackData,
		hasStudyPackContentAccess,
	);

	const { isLanguageLearningApp } = useAppTemplate();
	const itemTypesSectionData = useItemTypesSection();

	const sections: HomeSection[] = [
		...(tasksSectionData?.isEnabled
			? [
					{
						isLoading: tasksSectionData.isLoading,
						header: {
							title: tasksSectionData.sectionTitle,
						},
						type: "tasks" as const,
						data: [{ items: tasksSectionData.data }],
					},
				]
			: []),
		...(productsSectionData.isEnabled
			? [
					{
						isLoading: productsSectionData.isLoading,
						header: {
							title: productsSectionData.sectionTitle,
						},
						type: "products" as const,
						data: [{ items: productsSectionData.data }],
					},
				]
			: []),
		...(isLanguageLearningApp
			? itemTypesSectionData.isEnabled || itemTypesSectionData.isLoading
				? [
						{
							isLoading: itemTypesSectionData.isLoading,
							header: {
								title: itemTypesSectionData.sectionTitle,
							},
							type: "itemTypes" as const,
							data: [{ items: itemTypesSectionData.data }],
						},
					]
				: []
			: (topicsSectionData ?? [])
					.filter((s) => s.isEnabled || s.isLoading)
					.map((s) => ({
						isLoading: s.isLoading,
						header: { title: s.sectionTitle },
						type: "topics" as const,
						data: [{ items: s.data }],
						onPress: s.onPress,
					}))),
	];

	return {
		sections,
	};
};
